import { useEffect, useRef, useState } from "react";
import { useGameUi, type MissionView } from "./gameStore";
import { GAME_MODES, type GameModeId } from "../engine/modes";
import { trackEvent } from "../core/analytics";

interface GameController {
  start(modeId?: GameModeId): void;
  restart(): void;
  home(): void;
  boost(): boolean;
  destroy(): void;
}

const format = (value: number) => Math.floor(value).toLocaleString("fr-FR");

function MissionCard({ mission }: { mission: MissionView }) {
  return (
    <div className={`missionCard ${mission.done ? "missionDone" : ""}`}>
      <div className="missionIcon">{mission.icon}</div>
      <div className="missionName">{mission.label}</div>
      <div className="missionProg">
        {format(Math.min(mission.progress, mission.target))}/
        {format(mission.target)}
        {mission.done ? " ✓" : ""}
      </div>
    </div>
  );
}

function MissionList({ missions }: { missions: MissionView[] }) {
  return (
    <>
      {missions.map((mission) => (
        <MissionCard key={mission.type} mission={mission} />
      ))}
    </>
  );
}

function MissionMilestoneToast() {
  const { missionToast, phase } = useGameUi();
  const [dismissedId, setDismissedId] = useState<number | null>(null);
  useEffect(() => {
    if (!missionToast || phase !== "running") return;
    const id = missionToast.id;
    const timer = window.setTimeout(
      () => setDismissedId(id),
      missionToast.kind === "progress" ? 2100 : 3300,
    );
    return () => window.clearTimeout(timer);
  }, [missionToast, phase]);
  if (phase !== "running" || !missionToast || dismissedId === missionToast.id) return null;
  return (
    <div
      key={missionToast.id}
      className={`missionMilestone ${missionToast.kind}`}
      role="status"
      aria-live="polite"
    >
      <span className="missionMilestoneSpark left" aria-hidden="true">✦</span>
      <span className="missionMilestoneIcon" aria-hidden="true">{missionToast.icon}</span>
      <span className="missionMilestoneText">{missionToast.text}</span>
      <span className="missionMilestoneSpark right" aria-hidden="true">✦</span>
      <span className="missionMilestoneTimer" aria-hidden="true" />
    </div>
  );
}

function Hud() {
  const { hud, message, messageId } = useGameUi();

  return (
    <>
      <div id="ui">
        <div className="top">
          <div className="pill">
            <div className="speed">
              {hud.speed}
              <small>KM/H</small>
            </div>
          </div>
          <div className="pill score">
            SCORE<strong>{format(hud.score)}</strong>
            <span>
              x{hud.multiplier.toFixed(1)}
              {hud.doubleScore > 0 ? " · x2" : ""}
            </span>
            <span className="coinLine">● {hud.runTokens} PIÈCES</span>
          </div>
        </div>
        <div className="effectHud">
          {hud.boostBlend > 0.04 && (
            <div className="effectChip on">
              ⚡ NITRO {hud.boost.toFixed(1)}s
            </div>
          )}
          {hud.magnet > 0 && (
            <div className="effectChip on">
              ◉ MAGNET {hud.magnet.toFixed(1)}s
            </div>
          )}
          {hud.doubleScore > 0 && (
            <div className="effectChip on">
              ×2 SCORE {hud.doubleScore.toFixed(1)}s
            </div>
          )}
          {hud.reverse > 0 && (
            <div className="effectChip on">
              ↔ INVERSÉ {hud.reverse.toFixed(1)}s
            </div>
          )}
          {hud.speedLimit > 0 && (
            <div className="effectChip malus on">
              90 VITESSE LIMITÉE {hud.speedLimit.toFixed(1)}s
            </div>
          )}
          {hud.steeringLimit > 0 && (
            <div className="effectChip malus on">
              ⇆ DIRECTION LIMITÉE {hud.steeringLimit.toFixed(1)}s
            </div>
          )}
          {hud.chaos > 0 && (
            <div className="effectChip malus on">
              !? ANARCHIE {hud.chaos.toFixed(1)}s
            </div>
          )}
        </div>

        <MissionMilestoneToast />
        <div className="center">
          {hud.finalCountdown !== null && (
            <div
              key={hud.finalCountdown}
              className="finalCountdown"
              aria-live="assertive"
            >
              {hud.finalCountdown}
            </div>
          )}
          <div
            key={messageId}
            className={`pop ${message ? "show" : ""}`}
            aria-live="polite"
          >
            {message}
          </div>
          <div className="combo">
            {hud.combo > 1 ? `${hud.combo} passages serrés` : ""}
          </div>
        </div>
        <div className="bottom">
          <div className="meter">
            <div className="row">
              <span>{hud.combo > 0 ? `FLOW x${hud.combo}` : "FLOW"}</span>
              <span>{hud.nearMisses} PASSAGES SERRÉS</span>
            </div>
            <div className="bar">
              <div className="fill" style={{ width: `${hud.flowProgress}%` }} />
            </div>
          </div>
          <div className="runContext">
            <strong>{hud.modeLabel}</strong>
            <span>
              {hud.timeRemaining === null
                ? `NIVEAU ${hud.stage}`
                : `${Math.ceil(hud.timeRemaining)} s`}
            </span>
          </div>
          <div className="version">v0.24</div>
        </div>
      </div>
      <div
        id="boostFx"
        className={hud.boostBlend > 0.01 ? "on" : ""}
        style={{ opacity: Math.min(1, hud.boostBlend * 0.95) }}
      />
      <div
        id="flowFx"
        className={hud.combo > 0 ? "on" : ""}
        style={{ opacity: Math.min(0.72, hud.flowProgress / 150) }}
      />
    </>
  );
}

function Home({
  onStart,
  ready,
  engineError,
  selectedMode,
  onSelectMode,
}: {
  onStart: () => void;
  ready: boolean;
  engineError: boolean;
  selectedMode: GameModeId;
  onSelectMode: (modeId: GameModeId) => void;
}) {
  const { meta } = useGameUi();
  const isChrono = selectedMode === "timeAttack";
  return (
    <div className="screen">
      <div className="card">
        <div className="logo">
          RUSH<span>DRIVER</span>
        </div>
        <div className="tag">
          <strong>
            {isChrono
              ? "60 secondes pour faire exploser le score."
              : "Une route. Une erreur. Jusqu’où iras-tu ?"}
          </strong>
          <br />
          {isChrono
            ? "Nitro illimité. Évite les malus et garde la voiture sous contrôle."
            : "Frôle le trafic, construis ton Flow et bats ton record."}
        </div>
        <div className="modeSelect" aria-label="Mode de jeu">
          {GAME_MODES.map((mode) => (
            <button
              key={mode.id}
              type="button"
              className={`modeChoice ${selectedMode === mode.id ? "selected" : ""}`}
              aria-pressed={selectedMode === mode.id}
              onClick={() => onSelectMode(mode.id)}
            >
              <strong>{mode.label}</strong>
              <span>{mode.description}</span>
            </button>
          ))}
        </div>
        <button
          className="go"
          onClick={engineError ? () => window.location.reload() : onStart}
          disabled={!ready && !engineError}
        >
          {engineError
            ? "RÉESSAYER"
            : ready
              ? isChrono
                ? "LANCER LE CHRONO"
                : "PRENDRE LA ROUTE"
              : "CHARGEMENT…"}
        </button>
        <div className="homeStats">
          <div className="homeStat">
            RECORD<strong>{format(meta.best)}</strong>
          </div>
          <div className="homeStat">
            JETONS<strong>{format(meta.tokens)}</strong>
          </div>
          <div className="homeStat">
            MULTI DÉPART<strong>x{meta.multiplier.toFixed(1)}</strong>
          </div>
        </div>
        {isChrono ? (
          <div className="chronoBrief">
            <strong>OBJECTIF UNIQUE</strong>
            <span>
              Marque le plus de points en 60 secondes. Nitro illimité, malus à
              éviter.
            </span>
          </div>
        ) : (
          <div className="missionsBox">
            <div className="missionsHead">
              <span>OBJECTIFS · SET {meta.set}</span>
              <span>{meta.completed}/3</span>
            </div>
            <MissionList missions={meta.missions} />
          </div>
        )}
        <div className="powerLegend">
          <span className="powerTag">← → DIRIGER</span>
          <span className="powerTag">
            ⚡ ESPACE · {isChrono ? "NITRO ILLIMITÉ" : "NITRO"}
          </span>
          <span className="powerTag">FRÔLER · CHARGER LE FLOW</span>
        </div>
        <div
          className={`mini ${engineError ? "loadError" : ""}`}
          role={engineError ? "alert" : undefined}
        >
          {engineError
            ? "Le moteur 3D n’a pas pu démarrer. Recharge la page pour réessayer."
            : "v0.24 · progression sauvegardée sur cet appareil"}
        </div>
      </div>
    </div>
  );
}

function GameOver({
  onRestart,
  onHome,
}: {
  onRestart: () => void;
  onHome: () => void;
}) {
  const { meta, result } = useGameUi();
  const isChrono = result.modeLabel === "CHRONO";
  const completed = result.outcome === "complete";
  const seconds = Math.max(1, Math.round(result.duration));
  const remaining = Math.max(0, 60 - seconds);
  return (
    <div className="screen gameover" style={{ display: "grid" }}>
      <div className="card">
        <div className="resultMode">{result.modeLabel}</div>
        <h2>{completed ? "CHRONO TERMINÉ" : "COURSE TERMINÉE"}</h2>
        <p className="resultLead">
          {completed
            ? "60 secondes à fond. Voilà ce que tu as réussi à arracher."
            : isChrono
              ? `Collision après ${seconds} s. Il restait ${remaining} s à tenir.`
              : `${seconds} s sur la route avant l’impact.`}
        </p>
        <div className="resultScore">
          <span>SCORE</span>
          <strong>{format(result.score)}</strong>
          <small>{format(result.distance)} M PARCOURUS</small>
        </div>
        <div className="resultStats">
          <div className="resultStat">
            RECORD<strong>{format(meta.best)}</strong>
          </div>
          <div className="resultStat">
            JETONS<strong>+{result.runTokens}</strong>
          </div>
          <div className="resultStat">
            FRÔLEMENTS<strong>{result.nearMisses}</strong>
          </div>
          <div className="resultStat">
            MEILLEUR FLOW<strong>x{result.bestCombo}</strong>
          </div>
        </div>
        {!isChrono && (
          <div className="missionsBox">
            <div className="missionsHead">
              <span>OBJECTIFS · SET {meta.set}</span>
              <span>{meta.completed}/3</span>
            </div>
            <MissionList missions={meta.missions} />
          </div>
        )}
        <div className="resultActions">
          <button className="restart" onClick={onRestart}>
            {isChrono ? "REJOUER LE CHRONO" : "REPRENDRE LA ROUTE"}
          </button>
          <button className="backHome" onClick={onHome}>
            RETOUR AU MENU
          </button>
        </div>
      </div>
    </div>
  );
}

function Joystick() {
  const { joystick } = useGameUi();
  if (!joystick.visible) return null;
  return (
    <div
      id="joy"
      style={{ display: "block", left: joystick.x, top: joystick.y }}
    >
      <div
        id="knob"
        style={{
          transform: `translate(calc(-50% + ${joystick.dx}px), calc(-50% + ${joystick.dy}px))`,
        }}
      />
    </div>
  );
}

function NitroButton({ onActivate }: { onActivate: () => void }) {
  const { hud } = useGameUi();
  const ready = hud.unlimitedNitro || (hud.nitroEnergy >= 1 && hud.boost <= 0);
  return (
    <button
      type="button"
      className={`nitroButton ${ready ? "ready" : ""} ${hud.boost > 0 ? "active" : ""}`}
      disabled={!ready}
      onClick={onActivate}
      aria-label={
        hud.unlimitedNitro
          ? "Activer le Nitro illimité"
          : ready
            ? "Activer le Nitro"
            : `Nitro chargé à ${Math.round((hud.nitroEnergy / hud.nitroMaxEnergy) * 100)} pour cent`
      }
    >
      <span className="nitroLabel">NITRO</span>
      <span className="nitroCells" aria-hidden="true">
        {hud.unlimitedNitro ? (
          <span className="nitroInfinite">∞</span>
        ) : (
          Array.from({ length: hud.nitroMaxEnergy }, (_, index) => {
            const fill = Math.max(0, Math.min(1, hud.nitroEnergy - index));
            return (
              <span className="nitroCell" key={index}>
                <span style={{ transform: `scaleX(${fill})` }} />
              </span>
            );
          })
        )}
      </span>
      <small>ESPACE</small>
    </button>
  );
}

export function App() {
  const { phase } = useGameUi();
  const hostRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<GameController | null>(null);
  const [ready, setReady] = useState(false);
  const [engineError, setEngineError] = useState(false);
  const [selectedMode, setSelectedMode] = useState<GameModeId>("endless");

  useEffect(() => {
    let disposed = false;
    void import("../engine/main.js")
      .then(({ mountGame }) => {
        if (!disposed && hostRef.current) {
          gameRef.current = mountGame(hostRef.current);
          setReady(true);
        }
      })
      .catch(() => {
        if (!disposed) {
          trackEvent("engine_load_failed");
          setEngineError(true);
        }
      });
    return () => {
      disposed = true;
      gameRef.current?.destroy();
      gameRef.current = null;
    };
  }, []);

  return (
    <main className="gameShell">
      <div id="game-root" ref={hostRef} aria-hidden="true" />
      <Hud />
      <Joystick />
      {phase === "running" && (
        <NitroButton onActivate={() => gameRef.current?.boost()} />
      )}
      {phase === "home" && (
        <Home
          ready={ready}
          engineError={engineError}
          selectedMode={selectedMode}
          onSelectMode={(modeId) => {
            if (modeId !== selectedMode)
              trackEvent("mode_selected", {
                mode: modeId,
                previous_mode: selectedMode,
              });
            setSelectedMode(modeId);
          }}
          onStart={() => gameRef.current?.start(selectedMode)}
        />
      )}
      {phase === "gameover" && (
        <GameOver
          onRestart={() => gameRef.current?.restart()}
          onHome={() => gameRef.current?.home()}
        />
      )}
    </main>
  );
}
